"use client";

import { useEffect } from "react";
import LoadingIndicator from "src/components/common/LoadingIndicator/LoadingIndicator";
import Overlay from "src/components/common/Overlay/Overlay";
import Typography from "src/components/common/Typography/Typography";
import { useQueryParams } from "src/lib/hooks/ClientHooks";
import { useExtracted } from "#i18n";

interface TransitionInfo {
  heading: string;
  loading: boolean;
  persist?: boolean;
}

const GlobalTransitions = () => {
  const translateMessage = useExtracted();
  const { queryParams, setQueryParam } = useQueryParams();
  const transition = queryParams.get("transition");
  const transitions = {
    loggingIn: {
      heading: translateMessage("Signing in"),
      loading: true,
      persist: true,
    },
    loggingOut: {
      heading: translateMessage("Signing out"),
      loading: true,
    },
  } as const satisfies Record<string, TransitionInfo>;

  const transitionInfo: TransitionInfo | undefined = transition
    ? transitions[transition as keyof typeof transitions]
    : undefined;

  useEffect(() => {
    // if the user went back or forward in history, we need to reset the transition
    const callback = () => {
      setQueryParam("transition", null);
    };
    window.addEventListener("popstate", callback);
    return () => {
      window.removeEventListener("popstate", callback);
    };
  }, []);

  useEffect(() => {
    if (transition) {
      document.body.dataset.transition = transition;
    } else {
      delete document.body.dataset.transition;
    }
  }, [transition]);

  useEffect(() => {
    if (!transitionInfo?.persist) {
      setQueryParam("transition", null);
    }
  }, []);

  return (
    <>
      <style>
        {Object.keys(transitions)
          .map(
            (key) => `
        [data-global-overlay] {
          z-index: 1000;
        }
        
        [data-transition="${key}"] [data-global-overlay="${key}"] {
          pointer-events: all;
          visibility: visible;
          content-visibility: visible;
          opacity: 1;
         }`,
          )
          .join(" ")}
      </style>
      {Object.entries(transitions).map(([key, data]) => (
        <Overlay
          data-global-overlay={key}
          visible={transition === key}
          key={key}
          className={"!fixed"}
        >
          <Typography as="h2" variant="heading2">
            {data.heading}
          </Typography>
          {data.loading ? <LoadingIndicator className={"text-5xl"} /> : null}
        </Overlay>
      ))}
    </>
  );
};

export default GlobalTransitions;
